import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChartColumn, ChevronLeft, ChevronRight, Settings } from 'lucide-react';
import { useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import { FEATURES } from '../../utils/featureFlags';
import BrandWordmark from '../common/BrandWordmark';
import DatePickerPopup from '../DailyTracker/DatePickerPopup';
import { useSelectedDate } from '../../context/SelectedDateContext';
import { useDayCompletion } from '../../hooks/useDayCompletion';

const viewItems = [
  { to: '/statistics?tab=habits', labelId: 'app.nav.stats.habits' },
  { to: '/statistics?tab=notes', labelId: 'app.nav.stats.notes' },
] as const;

interface AppNavProps {
  settingsOpen: boolean;
  setSettingsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  settingsRef: React.Ref<HTMLDivElement>;
  settingsPanel: React.ReactNode;
}

function threeChars(value: string): string {
  return value.replace(/\./g, '').trim().slice(0, 3);
}

function longestLabel(labels: string[]): string {
  return labels.reduce((best, label) => (label.length > best.length ? label : best), '');
}

const AppNav: React.FC<AppNavProps> = ({
  settingsOpen,
  setSettingsOpen,
  settingsRef,
  settingsPanel,
}) => {
  const { formatMessage: t, formatDate: intlFormatDate } = useIntl();
  const { pathname } = useLocation();
  const isDailyPage = pathname === '/';
  const { selectedDate, setSelectedDate, goToToday } = useSelectedDate();
  const { completedItems, totalItems, getDayProgress } = useDayCompletion(selectedDate);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const datePickerBtnRef = useRef<HTMLButtonElement>(null);
  const statsMenuRef = useRef<HTMLDivElement>(null);

  const weekdayLabel = threeChars(intlFormatDate(selectedDate, { weekday: 'short' }));
  const monthLabel = threeChars(intlFormatDate(selectedDate, { month: 'short' }));
  const dayLabel = String(selectedDate.getDate());

  const dateSizers = useMemo(() => {
    const weekdays = Array.from({ length: 7 }, (_, i) =>
      threeChars(intlFormatDate(new Date(2024, 0, i + 1), { weekday: 'short' })),
    );
    const months = Array.from({ length: 12 }, (_, i) =>
      threeChars(intlFormatDate(new Date(2024, i, 1), { month: 'short' })),
    );
    return {
      weekday: longestLabel(weekdays),
      monthDay: `${longestLabel(months)} 30`,
    };
  }, [intlFormatDate]);

  const pct = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  useEffect(() => {
    if (!isDailyPage) setDatePickerOpen(false);
  }, [isDailyPage]);

  useEffect(() => {
    if (!statsOpen) return;
    const close = (event: MouseEvent) => {
      if (statsMenuRef.current?.contains(event.target as Node)) return;
      setStatsOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [statsOpen]);

  const shiftDay = (delta: number) => {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + delta);
    setSelectedDate(next);
  };

  return (
    <nav className="app-nav">
      <div className="app-nav__bar">
        <div className="app-nav__inner">
          <Link
            to="/"
            className="app-nav__brand"
            aria-label={t({ id: 'app.nav.home' })}
            onClick={goToToday}
          >
            <BrandWordmark className="app-nav__title-full" />
            <span className="app-nav__title-short">H</span>
          </Link>

          {isDailyPage && (
          <div className="app-nav__date">
            <button
              type="button"
              className="app-nav__day-nav"
              onClick={() => shiftDay(-1)}
              aria-label={t({ id: 'daily.prevDay' })}
            >
              <ChevronLeft size={18} />
            </button>
            <div className="app-nav__picker-wrap">
              <button
                ref={datePickerBtnRef}
                type="button"
                className="daily-date"
                onClick={() => setDatePickerOpen((open) => !open)}
                aria-label={t({ id: 'daily.selectDate' })}
                aria-expanded={datePickerOpen}
              >
                <span className="daily-date__sizer" aria-hidden="true">
                  <span className="daily-date__weekday">{dateSizers.weekday},</span>{' '}
                  <em className="daily-date__month">{dateSizers.monthDay}</em>
                </span>
                <span className="daily-date__value">
                  <span className="daily-date__weekday">{weekdayLabel},</span>{' '}
                  <em className="daily-date__month">{monthLabel} {dayLabel}</em>
                </span>
              </button>
              {datePickerOpen && (
                <DatePickerPopup
                  selectedDate={selectedDate}
                  onSelect={setSelectedDate}
                  onClose={() => setDatePickerOpen(false)}
                  excludeCloseRef={datePickerBtnRef}
                  getDayProgress={getDayProgress}
                />
              )}
            </div>
            <button
              type="button"
              className="app-nav__day-nav"
              onClick={() => shiftDay(1)}
              aria-label={t({ id: 'daily.nextDay' })}
            >
              <ChevronRight size={18} />
            </button>
          </div>
          )}

          <div className="app-nav__actions">
            <div className="app-nav__stats-wrapper" ref={statsMenuRef}>
              <button
                type="button"
                className={`app-nav__settings-trigger${statsOpen ? ' app-nav__settings-trigger--open' : ''}`}
                aria-label={t({ id: 'app.nav.statistics' })}
                aria-haspopup="menu"
                aria-expanded={statsOpen}
                onClick={() => setStatsOpen((open) => !open)}
              >
                <ChartColumn size={15} />
              </button>
              {statsOpen && (
                <div className="app-nav__stats-dropdown" role="menu">
                  {viewItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      role="menuitem"
                      className="app-nav__stats-item"
                      onClick={() => setStatsOpen(false)}
                    >
                      {t({ id: item.labelId })}
                    </Link>
                  ))}
                  {FEATURES.STATS_TASKS_TAB && (
                    <Link
                      to="/statistics?tab=tasks"
                      role="menuitem"
                      className="app-nav__stats-item"
                      onClick={() => setStatsOpen(false)}
                    >
                      {t({ id: 'app.nav.stats.tasks' })}
                    </Link>
                  )}
                </div>
              )}
            </div>

            <div className="app-nav__settings-wrap" ref={settingsRef}>
              <button
                className={`app-nav__settings-trigger${settingsOpen ? ' app-nav__settings-trigger--open' : ''}`}
                onClick={() => setSettingsOpen((open: boolean) => !open)}
                title={t({ id: 'settings.title' })}
                aria-label={t({ id: 'settings.open' })}
                aria-expanded={settingsOpen}
                aria-haspopup="dialog"
              >
                <Settings size={15} />
              </button>
              {settingsPanel}
            </div>
          </div>
        </div>

        <div
          className="app-nav__progress"
          style={{ '--progress-pct': `${pct}%` } as React.CSSProperties}
        >
          <div className="app-nav__progress-track">
            <div className="app-nav__progress-fill" />
          </div>
          {isDailyPage && totalItems > 0 ? (
            <span
              className={`app-nav__progress-flag${pct <= 8 ? ' app-nav__progress-flag--start' : pct >= 92 ? ' app-nav__progress-flag--end' : ''}`}
            >
              {t({ id: 'daily.progress.count' }, { completed: completedItems, total: totalItems })}
            </span>
          ) : null}
        </div>
      </div>
    </nav>
  );
};

export default AppNav;
